/* One cancellable lifecycle; Dawn still owns cart rendering and keyboard trapping. */
(() => {
  if (!document.body.classList.contains('rs2') || window.rs2Drawers) return;
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const states = new Map();
  let active = null;
  let scrollPosition = null;
  function restoreScroll() {
    if (scrollPosition && (window.scrollX !== scrollPosition.left || window.scrollY !== scrollPosition.top))
      window.scrollTo({...scrollPosition, behavior:'instant'});
  }
  let stopScrim = () => {};
  const scrim = document.createElement('div');
  scrim.className = 'rs2-drawer-scrim';
  scrim.setAttribute('aria-hidden', 'true');
  document.body.append(scrim);
  const duration = () => motion.matches ? 0 : 440;
  function state(drawer) {
    if (!states.has(drawer)) states.set(drawer, {timer:0, frame:0, stop:() => {}, trigger:null});
    return states.get(drawer);
  }
  function cancel(s) {
    clearTimeout(s.timer);
    cancelAnimationFrame(s.frame);
    s.stop();
    s.stop = () => {};
  }
  function panel(drawer) {
    return drawer.querySelector(drawer.matches('header-drawer') ? '#menu-drawer' : '.drawer__inner');
  }
  function positionMenu() {
    const header = document.querySelector('.section-header .header-wrapper') || document.querySelector('.section-header');
    if (!header) return;
    document.documentElement.style.setProperty('--header-bottom-position', `${Math.max(0, header.getBoundingClientRect().bottom)}px`);
  }
  function positionCart(drawer) {
    const icon = document.querySelector('#cart-icon-bubble');
    const target = panel(drawer);
    if (!icon || !target) return;
    const rect = icon.getBoundingClientRect();
    target.style.setProperty('--rs2-cart-close-left', `${rect.left + (rect.width - 44) / 2 - target.offsetLeft - target.clientLeft}px`);
    target.style.setProperty('--rs2-cart-close-top', `${rect.top + (rect.height - 44) / 2 - target.offsetTop - target.clientTop}px`);
  }
  function finish(drawer) {
    cancel(state(drawer));
    drawer.classList.remove('rs2-drawer-visible', 'active');
    const details = drawer.matches('header-drawer') && drawer.querySelector('details');
    if (details) {
      details.open = false;
      details.classList.remove('menu-opening');
      drawer.closest('.section-header')?.classList.remove('rs2-drawer-header');
    }
    if (![...states.keys()].some(item => item.classList.contains('rs2-drawer-visible'))) {
      document.documentElement.classList.remove('rs2-drawer-lock');
      document.body.classList.remove('rs2-drawer-lock');
      restoreScroll();
      scrollPosition = null;
    }
  }
  function close(drawer, immediate = false, restoreFocus = true) {
    if (drawer.dataset.rs2Open !== 'true') {
      if (immediate) finish(drawer);
      return;
    }
    const s = state(drawer);
    cancel(s);
    drawer.dataset.rs2Open = 'false';
    drawer.classList.remove('rs2-drawer-open');
    drawer.querySelector('details > summary')?.setAttribute('aria-expanded', 'false');
    const isActive = active === drawer;
    if (isActive) {
      active = null;
      removeTrapFocus();
      stopScrim();
      scrim.classList.remove('is-open');
      if (restoreFocus && s.trigger?.isConnected) s.trigger.focus({preventScroll:true});
    }
    if (immediate || motion.matches) finish(drawer);
    else s.timer = setTimeout(() => finish(drawer), duration());
  }
  function open(drawer, trigger) {
    const target = panel(drawer);
    if (!target) return;
    const openingScroll = scrollPosition || {left:window.scrollX, top:window.scrollY};
    const s = state(drawer);
    cancel(s);
    // Includes a drawer that is already fading out: no stale close can unlock the next one.
    for (const other of states.keys()) if (other !== drawer) close(other, true, false);
    scrollPosition = openingScroll;
    active = drawer;
    const menu = drawer.matches('header-drawer');
    s.trigger = menu ? document.querySelector('#rs-menu-toggle') : trigger || drawer.activeElement || s.trigger || document.activeElement;
    if (!menu && s.trigger) drawer.setActiveElement(s.trigger);
    document.documentElement.classList.add('rs2-drawer-lock');
    document.body.classList.add('rs2-drawer-lock');
    if (menu) {
      // Remove StickyHeader's transformed containing block before measuring a fixed panel.
      drawer.closest('.section-header')?.classList.add('rs2-drawer-header');
      positionMenu();
      const details = drawer.querySelector('details');
      details.open = true;
      details.classList.add('menu-opening');
      details.querySelector('summary').setAttribute('aria-expanded', 'true');
    }
    drawer.dataset.rs2Open = 'true';
    drawer.classList.add('rs2-drawer-visible', 'active');
    stopScrim();
    scrim.classList.add('is-open');
    if (!motion.matches) stopScrim = window.rs2DitherReveal?.(scrim, 440) || (() => {});
    // Force a closed starting pose before the next frame, including immediate reopen.
    void target.offsetWidth;
    restoreScroll();
    s.frame = requestAnimationFrame(() => {
      if (active !== drawer || drawer.dataset.rs2Open !== 'true') return;
      drawer.classList.add('rs2-drawer-open');
      if (!motion.matches) s.stop = window.rs2DitherReveal?.(target, 440) || (() => {});
      if (!menu) positionCart(drawer);
      const focusTarget = target.querySelector(menu ? 'input[type="search"]' : '.drawer__close') || target;
      const focusScope = menu ? drawer.closest('.header-wrapper') || target : target;
      trapFocus(focusScope, focusTarget, {preventScroll:true});
      restoreScroll();
    });
  }
  scrim.addEventListener('click', () => { if (active) close(active); });
  window.addEventListener('resize', () => {
    if (!active) return;
    if (active.matches('header-drawer')) positionMenu();
    else positionCart(active);
  });
  motion.addEventListener('change', () => {
    if (!motion.matches) return;
    stopScrim();
    for (const [drawer, s] of states) {
      s.stop();
      if (drawer.dataset.rs2Open !== 'true') finish(drawer);
    }
  });
  document.addEventListener('shopify:section:unload', event => {
    for (const drawer of states.keys()) if (event.target.contains(drawer)) {
      close(drawer, true, false);
      states.delete(drawer);
    }
  });
  window.rs2Drawers = {open, close};
})();
